import { useState } from 'react';
// @mui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
//
import Iconify from 'src/components/iconify';
import { IFTA_FILING_INSTRUCTIONS, IftaStateInstructions } from './ifta-filing-instructions';

// ----------------------------------------------------------------------

const BASE_STATE_STORAGE_KEY = 'ifta-base-jurisdiction';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function HowToFileDialog({ open, onClose }: Props) {
  const [state, setState] = useState<IftaStateInstructions | null>(() => {
    const saved = localStorage.getItem(BASE_STATE_STORAGE_KEY);
    return IFTA_FILING_INSTRUCTIONS.find((entry) => entry.code === saved) || null;
  });

  const selectState = (selected: IftaStateInstructions | null) => {
    setState(selected);
    if (selected) {
      localStorage.setItem(BASE_STATE_STORAGE_KEY, selected.code);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>How to file your IFTA return</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {state && (
            <Autocomplete
              options={IFTA_FILING_INSTRUCTIONS}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              value={state}
              onChange={(_, selected) => selectState(selected)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Your base jurisdiction"
                  data-testid="base-state-select"
                />
              )}
            />
          )}

          {state && (
            <Stack spacing={2} data-testid="state-instructions">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                IFTA in {state.name} is handled by the {state.agency}. You file one quarterly
                return with them; they settle up with every other state for you.
              </Typography>

              <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
                {state.steps.map((step, index) => (
                  <Typography key={index} component="li" variant="body2">
                    {step}
                  </Typography>
                ))}
              </Stack>

              {state.notes && (
                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                  {state.notes}
                </Typography>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  color="success"
                  href={state.portalUrl}
                  target="_blank"
                  rel="noopener"
                  startIcon={<Iconify icon="solar:link-bold" />}
                  data-testid="open-portal-button"
                >
                  Open {state.portalName}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  href={state.infoUrl}
                  target="_blank"
                  rel="noopener"
                >
                  {state.name} IFTA info page
                </Button>
              </Stack>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Returns are due the last day of the month after the quarter ends (Apr 30, Jul 31,
                Oct 31, Jan 31). Details can change — confirm on the official site. Both links go
                to the {state.name} government website.
              </Typography>
            </Stack>
          )}

          {!state && (
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Pick the state your fleet is registered in to see exactly where and how to file,
                with links to the official state website.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {IFTA_FILING_INSTRUCTIONS.map((entry) => (
                  <Chip
                    key={entry.code}
                    label={entry.name}
                    size="small"
                    variant="outlined"
                    onClick={() => selectState(entry)}
                    data-testid={`state-chip-${entry.code}`}
                  />
                ))}
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
